/**
 * Octopus IoT Platform - Geofence API Service
 * FASE 2: Frontend API calls for Geofence feature
 * 
 * Reference: docs/OCTOPUS_GEOFENCE_DESIGN_DOC.md
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const geofenceApi = {
  // ==================== GEOFENCE CRUD ====================
  
  /**
   * Upload KMZ/KML file to create a new geofence
   * @param {FormData} formData - file, name, description, color
   * Response: { primary_geofence, geofences[], created_count, source_group_id, source_group_name }
   */
  uploadGeofence: (formData) => axios.post(`${API}/geofences/upload`, formData),

  /**
   * Create a manual polygon or waypoint, optionally inside a group.
   * @param {object} data - { name, description?, color, geometry_type, polygon?, point?, source_group_id? }
   */
  createManualGeofence: (data) => axios.post(`${API}/geofences/manual`, data),

  /**
   * List geofence groups, including empty groups.
   */
  listGeofenceGroups: () => axios.get(`${API}/geofences/groups`),

  /**
   * Create an empty geofence group.
   * @param {object} data - { name, description? }
   */
  createGeofenceGroup: (data) => axios.post(`${API}/geofences/groups`, data),

  /**
   * Preview a KMZ/KML file to check metadata (e.g. embedded colors)
   * @param {File} file - The KMZ/KML file
   * Response: { geometry_count, has_colors, color_samples[] }
   */
  previewFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/geofences/preview`, fd);
  },

  /**
   * Parse a KMZ/KML file and return its full geometries (with coordinates).
   * Used when replacing an existing geofence's vertices from a file in-place.
   * @param {File} file - The KMZ/KML file
   * Response: { geometries: [{ name, description, geometry_type, polygon, point, color }], total }
   */
  parseFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/geofences/parse`, fd);
  },

  /**
   * Download a geofence as a KMZ file (blob). Mirrors routesApi.exportRoute.
   * @param {string} id - Geofence id
   */
  exportGeofence: (id) =>
    axios.get(`${API}/geofences/${id}/export`, { responseType: 'blob' }),

  /**
   * Toggle all geofences within a source group
   * @param {string} groupId - source_group_id
   * @param {boolean} enabled - target state
   */
  toggleGeofenceGroup: (groupId, enabled) =>
    axios.put(`${API}/geofences/groups/${groupId}/enabled`, { enabled }),

  /**
   * Update visual style for all geofences within a source group
   * @param {string} groupId - source_group_id
   * @param {object} style - { color, border_color, border_width, fill_enabled, fill_opacity }
   */
  updateGeofenceGroupStyle: (groupId, style) =>
    axios.put(`${API}/geofences/groups/${groupId}/style`, style),

  /**
   * Delete all geofences within a source group
   * @param {string} groupId - source_group_id
   */
  deleteGeofenceGroup: (groupId) =>
    axios.delete(`${API}/geofences/groups/${groupId}`),

  /**
   * Move one geofence into a group, or pass null to remove it from groups.
   * @param {string} id - Geofence UUID
   * @param {string|null} groupId - target source_group_id
   */
  moveGeofenceToGroup: (id, groupId) =>
    axios.put(`${API}/geofences/${id}/group`, { source_group_id: groupId }),
  
  /**
   * List all geofences
   * @param {boolean} enabledOnly - Filter to only enabled geofences
   */
  listGeofences: (enabledOnly = false) => 
    axios.get(`${API}/geofences`, { params: { enabled_only: enabledOnly } }),
  
  /**
   * Get a specific geofence by ID
   * @param {string} id - Geofence UUID
   */
  getGeofence: (id) => axios.get(`${API}/geofences/${id}`),
  
  /**
   * Update geofence properties
   * @param {string} id - Geofence UUID
   * @param {object} data - { name?, description?, color?, enabled? }
   */
  updateGeofence: (id, data) => axios.put(`${API}/geofences/${id}`, data),
  
  /**
   * Delete a geofence and all associated rules
   * @param {string} id - Geofence UUID
   */
  deleteGeofence: (id) => axios.delete(`${API}/geofences/${id}`),
  
  // ==================== GEOFENCE EVENTS & HISTORY ====================
  
  /**
   * Get transition events for a geofence
   * @param {string} id - Geofence UUID
   * @param {number} limit - Max events to return
   */
  getGeofenceEvents: (id, limit = 100) => 
    axios.get(`${API}/geofences/${id}/events`, { params: { limit } }),
  
  /**
   * Get stay history for a geofence (all equipment)
   * @param {string} id - Geofence UUID
   * @param {number} limit - Max records to return
   */
  getGeofenceHistory: (id, limit = 100) => 
    axios.get(`${API}/geofences/${id}/history`, { params: { limit } }),
  
  // ==================== EQUIPMENT GEOFENCE RULES ====================
  
  /**
   * Get all geofence rules for an equipment
   * @param {string} equipmentId - Equipment UUID
   */
  getEquipmentRules: (equipmentId) => 
    axios.get(`${API}/equipments/${equipmentId}/geofence-rules`),
  
  /**
   * Create a geofence rule linking equipment to geofence
   * @param {string} equipmentId - Equipment UUID
   * @param {object} data - { geofence_id, enabled, rules: [...] }
   */
  createEquipmentRule: (equipmentId, data) => 
    axios.post(`${API}/equipments/${equipmentId}/geofence-rules`, data),
  
  /**
   * Update a geofence rule
   * @param {string} equipmentId - Equipment UUID
   * @param {string} ruleId - Rule UUID
   * @param {object} data - { enabled?, rules?: [...] }
   */
  updateEquipmentRule: (equipmentId, ruleId, data) => 
    axios.put(`${API}/equipments/${equipmentId}/geofence-rules/${ruleId}`, data),
  
  /**
   * Delete a geofence rule
   * @param {string} equipmentId - Equipment UUID
   * @param {string} ruleId - Rule UUID
   */
  deleteEquipmentRule: (equipmentId, ruleId) => 
    axios.delete(`${API}/equipments/${equipmentId}/geofence-rules/${ruleId}`),
  
  // ==================== EQUIPMENT EVENTS & HISTORY ====================
  
  /**
   * Get geofence events for an equipment
   * @param {string} equipmentId - Equipment UUID
   * @param {number} limit - Max events to return
   */
  getEquipmentEvents: (equipmentId, limit = 100) => 
    axios.get(`${API}/equipments/${equipmentId}/geofence-events`, { params: { limit } }),
  
  /**
   * Get geofence history for an equipment
   * @param {string} equipmentId - Equipment UUID
   * @param {number} limit - Max records to return
   */
  getEquipmentHistory: (equipmentId, limit = 100) => 
    axios.get(`${API}/equipments/${equipmentId}/geofence-history`, { params: { limit } }),
  
  /**
   * Get zones where equipment is currently located
   * @param {string} equipmentId - Equipment UUID
   */
  getEquipmentCurrentZones: (equipmentId) => 
    axios.get(`${API}/equipments/${equipmentId}/geofence-history/current`),
  
  /**
   * Get consolidated geofence status for an equipment
   * @param {string} equipmentId - Equipment UUID
   */
  getEquipmentGeofenceStatus: (equipmentId) => 
    axios.get(`${API}/equipments/${equipmentId}/geofence-status`),
};

export default geofenceApi;
